/**
 * Every literal string the test bench page shows, in one file.
 *
 * Here rather than inline for one reason: this copy is the product. The page is
 * a list and two buttons; what makes it usable is whether the sentences are
 * right, and they are drafts written by someone who does not run these
 * conversations. Keeping them together means a correction is one file and no
 * hunt through JSX.
 *
 * 🔴 The rules these strings follow, from the design:
 *
 *  - State the recipient constantly. It appears in the header AND on the send
 *    button. The repetition is the point -- it is the safety property.
 *  - Never a raw Ruby class name, never a traceback. The server already refuses
 *    to send one; nothing here reintroduces it.
 *  - "Show me" answers "is the content right?". "Send it to me" answers "does it
 *    actually look right?". The page must not imply the first replaces the
 *    second -- Gmail and Outlook mangle CSS differently, and that gap is the
 *    whole reason verification happens in an inbox.
 */

export const PAGE_TITLE = 'Email Test Bench'
export const PAGE_SUBTITLE =
  'Trigger any platform email against real data and check it in your own inbox.'

export const SENDS_TO_YOU = 'Sends to you:'

export const FILTER_LABEL = (available: number) => `Filter ${available} available emails…`
export const NO_MATCHES = 'Nothing matches that.'

export const IDLE_TITLE = 'Pick an email'
export const IDLE_BODY = 'Choose one on the left to see what it is, and to send it to yourself.'

export const ACTION_PREVIEW = 'Show me'
export const ACTION_PREVIEW_SUB = 'renders here, sends nothing'
export const ACTION_SEND = 'Send it to me'
export const ACTION_SEND_SUB = (recipient: string) => `to ${recipient}`

/**
 * The detail pane's "About this email" section: what the thing IS, before you
 * configure it and fire it. The list on the left is for FINDING an email and
 * carries no prose at all -- 48 rows each holding a paragraph is not a list
 * anyone can scan.
 *
 * The heading is the ONLY chrome this section has, and it is rendered only when
 * there is something to put under it.
 */
export const ABOUT_HEADING = 'About this email'

/**
 * Prefixes the dormancy sentence inside the About section, where "dormant" as a
 * bare word would be the page's own jargon. The list still carries the amber
 * `dormant` chip; this is the same fact said in words for someone who has now
 * clicked the row.
 */
export const ABOUT_DORMANT = 'Not currently sending.'

export const CONTEXT_HEADINGS: Record<string, string> = {
  conversation: 'Conversation',
  topic: 'Topic',
  membership: 'Membership',
  none: 'Context',
  locked: 'Not available'
}

export const CONTEXT_NONE = "This email doesn't need a conversation — it only needs you."
export const CONTEXT_MEMBERSHIP =
  'This email renders against a membership record, and there is no picker for it — the bench uses the first one on the database.'
export const CONTEXT_CHANGE = 'Change'
export const CONTEXT_USING = 'Using'
export const CONTEXT_LOADING = 'Loading…'
export const CONTEXT_CONVERSATION_EMPTY =
  'There are no conversations on this database to render an email from.'
export const CONTEXT_TOPIC_EMPTY = 'No topic here has a conversation to render from.'

export const PREVIEW_CAVEAT =
  'This is the content the app generates. Mandrill wraps it in the template named above — the header image, the colours and the footer all come from there, and none of them are shown here. Send it to yourself to see the finished email.'

export const SENT_TITLE = 'Sent — check your inbox'
export const SENT_BODY = (recipient: string) =>
  `Delivered to ${recipient}. The subject names the real recipient it would have gone to, so you can tell test mail apart at a glance.`
export const SENT_NO_RECEIPT_TITLE = 'Sent, but no receipt row appeared'
export const SENT_NO_RECEIPT_BODY =
  'The send returned without recording a MailReceipt, which means it stopped somewhere before Mandrill. Treat an empty inbox as expected rather than as a mystery.'

export const REFUSED_TITLE = "Not sent — you're signed in as someone else"
export const UNAVAILABLE_TITLE = "Can't test this one"
export const FAILED_TITLE = "Couldn't build this one"
export const UNSUPPORTED_TITLE = 'Not available on this environment'
export const NETWORK_TITLE = "Couldn't reach the server"
export const NETWORK_BODY = 'The page has nothing to show until that request succeeds. Try again.'
export const FORBIDDEN_TITLE = 'You need super admin for this page'
export const FORBIDDEN_BODY =
  'The test bench can send platform email, so it is restricted further than the rest of the admin.'

export const RESOLVES_TO = 'resolves to'
export const LABEL_TO = 'To'
export const LABEL_FROM = 'From'
export const LABEL_SUBJECT = 'Subject'
export const LABEL_TEMPLATE = 'Template'
export const LABEL_REPLY_TO = 'Reply-To'
export const WOULD_HAVE_GONE_TO = 'Would have gone to'
export const RECEIPT = 'MailReceipt'
export const WARNINGS_TITLE = 'Worth knowing'
